import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Science as ScienceIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BusinessCenter as BusinessCenterIcon,
  Landscape as LandscapeIcon,
  Place as PlaceIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import clientService from 'src/clientService';
import { DeploymentDetailResponse, SoilSampleDetailResponse, SoilSampleResultResponse } from 'src/api/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';
import SoilSampleDialog from './SoilSampleDialog';
import { SoilElementsCatalogModal } from './SoilElementsCatalogModal';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';

export enum SoilSampleStatus {
  COLLECTED = 'Collected',
  IN_TRANSIT = 'In-Transit',
  LAB_ANALYSIS = 'Lab-Analysis',
  COMPLETED = 'Completed',
}

interface SoilChemistryDeploymentViewProps {
  masterData: DeploymentDetailResponse;
}

const getStatusChip = (status?: string | null) => {
  switch (status) {
    case SoilSampleStatus.COLLECTED:
      return <Chip label="Collected" size="small" color="info" />;
    case SoilSampleStatus.IN_TRANSIT:
      return <Chip label="In-Transit" size="small" color="warning" />;
    case SoilSampleStatus.LAB_ANALYSIS:
      return <Chip label="Lab Analysis" size="small" color="primary" />;
    case SoilSampleStatus.COMPLETED:
      return <Chip label="Completed" size="small" color="success" />;
    default:
      return <Chip label={status || 'Unknown'} size="small" />;
  }
};

const SoilChemistryDeploymentView: FC<SoilChemistryDeploymentViewProps> = ({ masterData }) => {
  const { project, site, location, deployment, device } = masterData;
  const { showSnackbar } = useSnackbar();

  const [samples, setSamples] = useState<SoilSampleDetailResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Dialog states
  const [isSampleDialogOpen, setIsSampleDialogOpen] = useState<boolean>(false);
  const [editingSample, setEditingSample] = useState<SoilSampleDetailResponse | null>(null);
  const [viewingSample, setViewingSample] = useState<SoilSampleDetailResponse | null>(null);
  const [isCatalogOpen, setIsCatalogOpen] = useState<boolean>(false);
  const [deletingSample, setDeletingSample] = useState<SoilSampleDetailResponse | null>(null);

  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clientService.request<SoilSampleDetailResponse[]>({
        path: `/api/v1/soil-chemistry/deployments/${deployment.deployment_id}/samples`,
        method: 'GET',
        format: 'json',
      });
      const data = response.data;
      if (Array.isArray(data)) {
        setSamples(data);
      } else if (data && Array.isArray((data as any).data)) {
        setSamples((data as any).data);
      } else {
        setSamples([]);
      }
    } catch (err) {
      console.error('Failed to fetch deployment soil samples:', err);
      showSnackbar({
        message: 'Failed to load soil samples for this deployment.',
        type: SnackbarType.ERROR,
      });
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [deployment.deployment_id, showSnackbar]);

  useEffect(() => {
    fetchSamples();
  }, [fetchSamples]);

  const handleOpenAddSample = () => {
    setEditingSample(null);
    setIsSampleDialogOpen(true);
  };

  const handleOpenEditSample = (sample: SoilSampleDetailResponse) => {
    setEditingSample(sample);
    setIsSampleDialogOpen(true);
  };

  const handleSampleSuccess = (updatedSample: SoilSampleDetailResponse) => {
    showSnackbar({
      message: `Soil sample "${updatedSample.sample_name}" saved successfully!`,
      type: SnackbarType.SUCCESS,
    });
    fetchSamples();
  };

  const handleDeleteSample = async () => {
    if (!deletingSample) return;
    try {
      await clientService.api.deleteSoilSampleApiV1SoilChemistrySamplesSampleIdDelete(
        deletingSample.sample_id
      );
      showSnackbar({
        message: 'Soil sample deleted successfully.',
        type: SnackbarType.SUCCESS,
      });
      fetchSamples();
    } catch (err) {
      console.error('Failed to delete soil sample:', err);
      showSnackbar({
        message: 'Failed to delete soil sample.',
        type: SnackbarType.ERROR,
      });
    } finally {
      setDeletingSample(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Deployment & Location Info Header */}
      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h1" fontWeight="bold">
            Soil Chemistry Deployment Details
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessCenterIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Project:</strong>{' '}
                <Link to={`/admin/project/${project.project_id}`} style={{ color: 'secondary' }}>
                  {project.project_name}
                </Link>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LandscapeIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Site:</strong>{' '}
                <Link
                  to={`/admin/project/${project.project_id}/site/${site.site_id}`}
                  style={{ color: 'secondary' }}
                >
                  {site.site_name}
                </Link>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Location:</strong> {location.location_name}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Deployment:</strong> {deployment.deployment_name} ({device.human_readable_id} -{' '}
                {device.type})
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonthIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Period:</strong> {formatDate(deployment.start_date)} -{' '}
                {formatDate(deployment.end_date)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Bar & Table */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Soil Samples ({samples.length})
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ScienceIcon />}
            onClick={() => setIsCatalogOpen(true)}
          >
            Soil Elements Catalog
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddSample}
          >
            Add Soil Sample
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : samples.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No soil samples recorded for this deployment yet. Click <strong>"Add Soil Sample"</strong> to create one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Sample Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Collected Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Soil Density (g/cm³)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Depth (cm)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Lab Results</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {samples.map((sample) => (
                <TableRow key={sample.sample_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{sample.sample_name}</TableCell>
                  <TableCell>
                    {sample.collected_date ? formatDate(sample.collected_date) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {sample.soil_density !== undefined && sample.soil_density !== null
                      ? sample.soil_density
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {sample.depth !== undefined && sample.depth !== null ? sample.depth : '-'}
                  </TableCell>
                  <TableCell>{getStatusChip(sample.status)}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${sample.results?.length || 0} Elements`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Lab Report">
                      <IconButton
                        size="small"
                        onClick={() => setViewingSample(sample)}
                        color="info"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Sample">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditSample(sample)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Sample">
                      <IconButton
                        size="small"
                        onClick={() => setDeletingSample(sample)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Sample Modal */}
      <SoilSampleDialog
        open={isSampleDialogOpen}
        onClose={() => setIsSampleDialogOpen(false)}
        sample={editingSample}
        onSuccess={handleSampleSuccess}
        initialProjectId={project.project_id}
        initialSiteId={site.site_id}
        initialLocationId={location.location_id}
        initialDeploymentId={deployment.deployment_id}
      />

      {/* Soil Elements Catalog Modal */}
      <SoilElementsCatalogModal open={isCatalogOpen} onClose={() => setIsCatalogOpen(false)} />

      {/* View Detail Report Modal */}
      {viewingSample && (
        <Dialog
          open={!!viewingSample}
          onClose={() => setViewingSample(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              display: 'flex',
              justify: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceIcon color="primary" />
              <Typography variant="h6">{viewingSample.sample_name}</Typography>
            </Box>
            {getStatusChip(viewingSample.status)}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Deployment
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {deployment.deployment_name}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Location / Site
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {location.location_name} ({site.site_name})
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Collected Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {viewingSample.collected_date ? formatDate(viewingSample.collected_date) : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Soil Density / Depth
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {viewingSample.soil_density || '-'} g/cm³ | {viewingSample.depth || '-'} cm
                </Typography>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1.5 }}>
              Laboratory Element Measurements
            </Typography>

            {!viewingSample.results || viewingSample.results.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No chemical measurements recorded yet.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {viewingSample.results.map((res: SoilSampleResultResponse) => (
                  <Grid item xs={12} sm={6} md={4} key={res.result_id}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {res.element?.name || 'Element'} ({res.element?.symbol})
                        </Typography>
                        {res.interpretation && (
                          <Chip
                            label={res.interpretation}
                            size="small"
                            color={
                              res.interpretation.toLowerCase().includes('optimal')
                                ? 'success'
                                : res.interpretation.toLowerCase().includes('high')
                                ? 'warning'
                                : res.interpretation.toLowerCase().includes('low')
                                ? 'error'
                                : 'default'
                            }
                          />
                        )}
                      </Box>
                      <Typography variant="h6" color="primary.main">
                        {res.value} {res.unit}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {viewingSample.notes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Notes
                </Typography>
                <Typography variant="body2">{viewingSample.notes}</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={!!deletingSample}
        onCancel={() => setDeletingSample(null)}
        onConfirm={handleDeleteSample}
        title="Delete Soil Sample"
        content={`Are you sure you want to delete "${deletingSample?.sample_name}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default SoilChemistryDeploymentView;
